import { State, Action, StateContext, Selector, NgxsOnInit, createSelector } from '@ngxs/store';
import { EditPaintStateModel } from './edit.paint.state.model';
import { SetCurrentRecipe, ClearCurrentRecipe, ResetPaint, SetPaintBrushMode, SetRecentRecipes, SetStoryRecipesForChapter, ResetStoryRecipes, AddRecentRecipe } from './edit.paint.actions';
import { PaintPresetRed } from 'src/app/engine/models/paint-presets.const';
import { PaintBrushMode } from '../../models/paint-brush-mode.enum';
import { Injectable } from '@angular/core';
import { EngineColorRecipeModel } from '../../models/engine-color-recipe.model';
import { LocalStorageEnum, StorageService } from 'src/app/shared/storage.service';
import { get, isNil, uniq } from 'lodash';
import { ColorRecipeModel } from '../../models/color-recipe.model';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { StoryChaptersStateModel } from 'src/app/story/state/chapters/story.chapters.state.model';



const initialState: EditPaintStateModel = {
  currentRecipe: PaintPresetRed.recipe,
  brushMode: PaintBrushMode.Modify,
  storyRecipes: {},
  recentRecipes: [],
  uniqueStoryRecipes: []
};

@State<EditPaintStateModel>({
  name: 'editPaint',
  defaults: initialState 
})
@Injectable()
export class EditPaintState implements NgxsOnInit {
  /**
   * Selectors
   */
  @Selector()
  static getCurrentRecipe(state: EditPaintStateModel) {
    return state.currentRecipe;
  }

  @Selector()
  static getCurrentRecipeForEngine(state: EditPaintStateModel) {
    return ColorRecipeModel.toEngine(state.currentRecipe);
  }

  @Selector()
  static getPaintBrushMode(state: EditPaintStateModel) {
    return state.brushMode;
  }

  @Selector()
  static getRecentRecipes(state: EditPaintStateModel) {
    return state.recentRecipes; // Most recent should be first
  }

  @Selector()
  static getStoryRecipes(state: EditPaintStateModel) {
    return state.storyRecipes;
  }

  @Selector()
  static getUniqueStoryRecipes(state: EditPaintStateModel) {
    return state.uniqueStoryRecipes;
  }

  @Selector([StoryChaptersState])
  static getCurrentChapterRecipes(editPaintState: EditPaintStateModel, storyChaptersState: StoryChaptersStateModel) {
    return editPaintState.storyRecipes[storyChaptersState.selectedChapter];
  }

  static getChapterRecipes(chapterId: string) {
    return createSelector([EditPaintState], (state: EditPaintStateModel) => {
      return state.storyRecipes[chapterId];
    });
  }

  /**
   * Actions
   */
  @Action(SetCurrentRecipe)
  setCurrentRecipe(context: StateContext<EditPaintStateModel>, { colorRecipe }: SetCurrentRecipe) {
    context.patchState({
      currentRecipe: colorRecipe
    });
  }

  @Action(SetPaintBrushMode)
  setBrushMode(context: StateContext<EditPaintStateModel>, action: SetPaintBrushMode) {
    context.patchState({
      brushMode: action.brushMode
    });
  }

  @Action(ClearCurrentRecipe)
  clearCurrentRecipe(context: StateContext<EditPaintStateModel>, action: ClearCurrentRecipe) {
    context.patchState({
      currentRecipe: null
    });
  }
  
  @Action(ResetPaint)
  resetPaint(context: StateContext<EditPaintStateModel>, action: ResetPaint) {
    context.setState(initialState);
  }


  @Action(AddRecentRecipe)
  addRecentRecipe(context: StateContext<EditPaintStateModel>, action: AddRecentRecipe) {
    let recentRecipes = context.getState().recentRecipes;
    const exists = recentRecipes.some(recentRecipe => EngineColorRecipeModel.isEqual(recentRecipe, action.recipe));

    // Do nothing with a recipes already in the recents
    if (exists) {
      return;
    }

    // Otherwise, add to the beginning of the array so that most recent paints show up first
    recentRecipes.splice(0, 0, action.recipe);

    // Limit recent paints to 14 (two rows worth)
    if (recentRecipes.length > 14) {
      recentRecipes = recentRecipes.slice(0, 14);
    }

    this.storageService.setLocalItem(LocalStorageEnum.RecentRecipes, recentRecipes);

    context.patchState({
      recentRecipes: [...recentRecipes]
    });
  }

  @Action(SetRecentRecipes)
  setRecentRecipes(context: StateContext<EditPaintStateModel>, action: SetRecentRecipes) {
    const recipes = isNil(action.recipes) ? [] : [...action.recipes];

    context.patchState({
      recentRecipes: recipes
    });

    this.storageService.setLocalItem(LocalStorageEnum.RecentRecipes, recipes);
  }

  @Action(SetStoryRecipesForChapter)
  setStoryRecipesForChapter(context: StateContext<EditPaintStateModel>, action: SetStoryRecipesForChapter) {
    const recipes = EngineColorRecipeModel.generateUniqueRecipes([...action.recipes]);
    const currentRecipes = context.getState().storyRecipes;
    currentRecipes[action.chapterId] = isNil(recipes) ? [] : [...recipes];

    context.patchState({
      storyRecipes: currentRecipes
    });

    // Calculate unique recipes only when story recipes change
    let uniqueRecipes = Object.values(currentRecipes)
      .reduce((result, chapterRecipes) => {
        result = [...result, ...chapterRecipes];
        return result;
      }, []);
    uniqueRecipes = EngineColorRecipeModel.generateUniqueRecipes(uniqueRecipes);
    uniqueRecipes.sort(EngineColorRecipeModel.compare);
      
    context.patchState({
      uniqueStoryRecipes: uniqueRecipes
    });
  }

  @Action(ResetStoryRecipes)
  resetStoryRecipes(context: StateContext<EditPaintStateModel>, action: ResetStoryRecipes) {
    context.patchState({
      storyRecipes: {}
    });
  }

  ngxsOnInit(context: StateContext<EditPaintStateModel>) {
    const paints = this.storageService.getLocalJSON(LocalStorageEnum.RecentRecipes);
    context.dispatch(new SetRecentRecipes(paints));
  }
  
  constructor(
    private storageService: StorageService
  ) {}
}
